import {
  ApiV1_Promotion,
  AppliedPromotion,
  BuyXPayYConfig,
  EvaluationContext,
  NthUnitConfig,
  ProgressiveConfig,
  PromotionCart,
  PromotionResult,
} from '../promotion.types';
import {
  getEffectivePromotionFamilies,
  roundToCents,
  toPromotionInput,
} from '../promotion-display-utils';
import { aggregateCartByProduct } from './aggregate-cart';
import { evaluateConditions } from './evaluate-conditions';
import { resolveProductFamilyWinner } from './resolve-family-winner';
import { UnitDiscount } from './family-outcome.types';

interface PerPromoProductCollection {
  promotion: ApiV1_Promotion;
  productId: string;
  unitDiscounts: UnitDiscount[];
  rawTotal: number;
  tierPercentage?: number;
}

/**
 * Applies promotions to a cart using the aggregate-based two-level engine.
 *
 * For each product in the cart:
 *   1. Aggregate units (`aggregateCartByProduct`).
 *   2. Merge promos per family via `getEffectivePromotionFamilies`.
 *   3. Run each family's calculator against the aggregate.
 *   4. Pick the winning family (highest totalDiscount; tiebreak by family order
 *      buyXPayY > nthUnit > progressive).
 *   5. Roll up the winner's unit discounts into per-(promo, product) entries.
 *
 * The output is **one `AppliedPromotion` per (promotion, product)**: a promo
 * scoped to several products yields one self-consistent entry per product (its
 * own discount, affected item ids and — for progressive — applied tier). This
 * mirrors how the engine already reasons (per product aggregate) instead of
 * merging products back into a single ambiguous entry.
 *
 * Rounding: per-unit math stays raw; we round once at the end and distribute the
 * residual cent deterministically via largest-remainder, so both invariants hold:
 *   - Σ discountByItemId == discountAmount for each AppliedPromotion
 *   - Σ AppliedPromotion.discountAmount == totalDiscount
 */
export function applyToCart(
  promotions: ApiV1_Promotion[],
  cart: PromotionCart,
  context: EvaluationContext,
): PromotionResult {
  const qualifying = promotions.filter(p => p.isActive && evaluateConditions(p, context));
  if (qualifying.length === 0 || cart.items.length === 0) {
    return emptyResult(cart);
  }

  const aggregates = aggregateCartByProduct(cart);
  if (aggregates.size === 0) {
    return emptyResult(cart);
  }

  const promosById = new Map(qualifying.map(p => [p.id, p]));
  const promoInputsById = new Map(qualifying.map(p => [p.id, toPromotionInput(p)]));

  const collectionsByKey = new Map<string, PerPromoProductCollection>();

  for (const [productId, aggregate] of aggregates) {
    const promosForProduct = qualifying.filter(p => p.scope.productIds.includes(productId));
    if (promosForProduct.length === 0) {
      continue;
    }

    const inputs = promosForProduct.map(p => promoInputsById.get(p.id)!);
    const families = getEffectivePromotionFamilies(inputs);

    const { winner } = resolveProductFamilyWinner(families, aggregate);
    if (!winner) {
      continue;
    }

    for (const breakdown of winner.perPromoBreakdown) {
      const promotion = promosById.get(breakdown.promotionId);
      if (!promotion) {
        continue;
      }
      const key = `${breakdown.promotionId}::${productId}`;
      let collection = collectionsByKey.get(key);
      if (!collection) {
        collection = { promotion, productId, unitDiscounts: [], rawTotal: 0 };
        collectionsByKey.set(key, collection);
      }
      collection.unitDiscounts.push(...breakdown.unitDiscounts);
      collection.rawTotal += breakdown.discountAmount;
      if (promotion.discountType === 'progressive') {
        const tier = deriveProgressiveTierPercentage(breakdown.unitDiscounts);
        if (tier != null) {
          collection.tierPercentage = tier;
        }
      }
    }
  }

  const appliedPromotions: AppliedPromotion[] = [];
  for (const collection of collectionsByKey.values()) {
    const { discountByItemId, roundedTotal } = buildRoundedDiscountByItemId(
      collection.unitDiscounts,
      collection.rawTotal,
    );
    if (roundedTotal <= 0) {
      continue;
    }
    const appliedPromotion: AppliedPromotion = {
      promotionId: collection.promotion.id,
      productId: collection.productId,
      name: collection.promotion.name,
      discountType: collection.promotion.discountType,
      discountAmount: roundedTotal,
      affectedItemIds: Object.keys(discountByItemId),
      discountByItemId,
      configSnapshot: getConfigSnapshot(collection.promotion),
    };
    if (collection.tierPercentage != null) {
      appliedPromotion.appliedTierPercentage = collection.tierPercentage;
    }
    appliedPromotions.push(appliedPromotion);
  }
  appliedPromotions.sort(
    (a, b) =>
      a.promotionId.localeCompare(b.promotionId) ||
      (a.productId ?? '').localeCompare(b.productId ?? ''),
  );

  const totalDiscount = roundToCents(
    appliedPromotions.reduce((sum, ap) => sum + ap.discountAmount, 0),
  );

  return {
    appliedPromotions,
    totalDiscount,
    originalTotal: cart.subtotal,
    finalTotal: Math.max(0, cart.subtotal - totalDiscount),
  };
}

/**
 * Rolls up unit-level discounts into a per-cartItem map, rounded to 2 decimals,
 * preserving the invariant `Σ values == roundedTotal` via largest-remainder
 * distribution. Determinism: ties on fractional remainder broken by stable
 * lexicographic order of the cartItemId.
 *
 * Negative-residual safety: we sort by largest fraction first in BOTH
 * directions. For negative residual (we need to subtract cents) this targets
 * entries that were rounded UP (fraction ≥ 0.5) before any entry that was
 * rounded DOWN (fraction < 0.5, potentially with `roundedCents == 0`). Without
 * this, the smallest-fraction-first sort could subtract from an entry whose
 * `roundedCents` is already 0, producing a negative per-item discount — a
 * semantically broken value (the invariant Σ still holds but the line shows
 * "ahorrás −$0.01").
 */
function buildRoundedDiscountByItemId(
  unitDiscounts: UnitDiscount[],
  rawTotal: number,
): { discountByItemId: Record<string, number>; roundedTotal: number } {
  const rawByItem = new Map<string, number>();
  for (const ud of unitDiscounts) {
    const key = ud.unit.cartItemId;
    rawByItem.set(key, (rawByItem.get(key) ?? 0) + ud.amount);
  }

  const entries = [...rawByItem.entries()].map(([itemId, rawAmount]) => ({
    itemId,
    rawAmount,
    roundedCents: Math.round(rawAmount * 100),
  }));

  const targetTotalCents = Math.round(rawTotal * 100);
  const sumRoundedCents = entries.reduce((sum, e) => sum + e.roundedCents, 0);
  let residual = targetTotalCents - sumRoundedCents;

  if (residual !== 0 && entries.length > 0) {
    entries.sort((a, b) => {
      const fracA = a.rawAmount * 100 - Math.floor(a.rawAmount * 100);
      const fracB = b.rawAmount * 100 - Math.floor(b.rawAmount * 100);
      const cmp = fracB - fracA;
      if (cmp !== 0) {
        return cmp;
      }
      return a.itemId.localeCompare(b.itemId);
    });

    const direction = residual > 0 ? 1 : -1;
    let cursor = 0;
    while (residual !== 0 && cursor < entries.length) {
      entries[cursor].roundedCents += direction;
      residual -= direction;
      cursor++;
    }
  }

  const discountByItemId: Record<string, number> = {};
  for (const entry of entries) {
    discountByItemId[entry.itemId] = entry.roundedCents / 100;
  }
  // roundToCents(rawTotal) === targetTotalCents / 100 — the per-(promo,product)
  // total the cart displays. Same convention the upsell hint uses for its total.
  return { discountByItemId, roundedTotal: roundToCents(rawTotal) };
}

function emptyResult(cart: PromotionCart): PromotionResult {
  return {
    appliedPromotions: [],
    totalDiscount: 0,
    originalTotal: cart.subtotal,
    finalTotal: cart.subtotal,
  };
}

/**
 * The applied progressive tier percentage, derived from any affected unit.
 * A progressive tier applies a uniform `basePrice × (pct / 100)` to every unit,
 * so `amount / basePrice × 100` recovers the exact tier percentage (the
 * unitDiscounts here are raw, pre-rounding). Returns null when there is no unit
 * with a positive base price to sample.
 */
function deriveProgressiveTierPercentage(unitDiscounts: UnitDiscount[]): number | null {
  const sample = unitDiscounts.find(ud => ud.unit.basePrice > 0);
  if (!sample) {
    return null;
  }
  return Math.round((sample.amount / sample.unit.basePrice) * 100);
}

function getConfigSnapshot(
  promotion: ApiV1_Promotion,
): BuyXPayYConfig | ProgressiveConfig | NthUnitConfig {
  switch (promotion.discountType) {
    case 'buyXPayY':
      return promotion.buyXPayYConfig;
    case 'progressive':
      return promotion.progressiveConfig;
    case 'nthUnit':
      return promotion.nthUnitConfig;
  }
}
